import React, { useRef, useState, useEffect, FormEvent } from 'react';
import { useRouter } from 'next/router';
import { getRoute } from '#utils';
import s from '#modules/search/search.module.scss';
import useOnClickOutside from '#hooks/useOnClickOutside';
import { useOvermindActions, useOvermindState } from '#store/overmind';
import SearchInput from '#components/inputs/search-input/SearchInput';
import Divider from '#components/divider/Divider';
import { animated, useSpring } from '@react-spring/web';
import { Teaser } from '#types/generated/graphql-types';
import useTranslation from 'next-translate/useTranslation';
import { useIsSearch } from '#hooks/useIsSearch';

const MIN_QUERY_LENGTH = 1;

const Search = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isSearchPage, searchQuery } = useIsSearch();

  const {
    app: { searchOpen },
    search: { result, loading, query },
  } = useOvermindState();

  const {
    app: { toggleSearch, initSearchQuery },
    search: { search, reset },
  } = useOvermindActions();

  const inputRef = useRef<HTMLInputElement | undefined>();
  const wrapperRef = useRef();
  const [active, setActive] = useState(false);

  const animation = useSpring({
    translateY: searchOpen || isSearchPage ? '0%' : '-200%',
  });

  useOnClickOutside(
    wrapperRef,
    (event: React.MouseEvent & { target: HTMLElement }) => {
      setActive(false);

      if (searchOpen) {
        if (event.target.getAttribute('data-role') === 'search-button') {
          return;
        }
        if (searchOpen) {
          toggleSearch(false);
        }
      }
    }
  );

  useEffect(() => {
    if (inputRef.current) {
      if (searchOpen === true) {
        inputRef.current.focus();
      } else {
        inputRef.current.blur();
        setActive(false);
        reset();
      }
    }
  }, [searchOpen]);

  useEffect(() => {
    if (isSearchPage) {
      initSearchQuery(searchQuery);
    } else {
      reset();
    }
  }, [searchQuery, isSearchPage]);

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    gotoSearchResultPage();
  };

  const handleInputChange = (value: string) => {
    if (!value) {
      reset();
      return;
    }

    search({ query: value });
  };

  const handleInputSubmit = () => {
    gotoSearchResultPage();
  };

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setActive(event.target === inputRef.current);
  };

  const handleInputClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSuggestionClick = (item: Teaser) => {
    toggleSearch(false);
    reset();
    router.push(getRoute(item, t));
  };

  const gotoSearchResultPage = () => {
    if (query.trim().length >= MIN_QUERY_LENGTH) {
      const url = `/${t('search').trim().toLowerCase()}/${encodeURIComponent(
        query
      )}`;
      toggleSearch(false);
      if (router.asPath !== url) {
        router.push(url);
      }
      if (inputRef.current) {
        inputRef.current.blur();
        setActive(false);
      }
    }
  };

  return (
    <div className={`${s.host} ${isSearchPage && s.hostSearch}`}>
      <animated.div ref={wrapperRef} className={s.inner} style={animation}>
        <form className={s.panel} onSubmit={handleFormSubmit} action="">
          <div className={s.panelContent}>
            <div className="tw-flex">
              <SearchInput
                loading={loading}
                classes={s.inputContainer}
                ref={inputRef}
                value={query}
                onChange={handleInputChange}
                onSubmit={handleInputSubmit}
                inputProps={{
                  onFocus: handleInputFocus,
                  onClick: handleInputClick,
                }}
              />
            </div>
            {result.length > 0 && active === true && (
              <>
                <Divider classes="tw-mt-1 tw-mb-2" />
                <ol className={s.options} data-testid="search-options">
                  {result.map((item, index) => (
                    <li className={s.option} key={`${item['@id']}-${index}`}>
                      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                      <button
                        className={s.optionButton}
                        tabIndex={index}
                        onClick={() => handleSuggestionClick(item)}
                      >
                        {item.title}
                      </button>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
        </form>
      </animated.div>
    </div>
  );
};

export default Search;
